/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { compose } from 'react-apollo';
import {
  shape, string, bool, func,
} from 'prop-types';
import { withTracking, loginLightboxLoadTrack, forgotPasswordLoginResetPasswordStep1Event } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import {
  ForgotExpiredLink as ForgotExpiredLinkPage,
  CreateNewPassword as CreateNewPasswordPage, Loading as LoadingPage,
  OTP as OTPPage,
  EmailVerification as EmailVerificationPage,
  ForgotPassword,
} from '@hyphengroup/cag-components';
import {
  withContinuePasswordReset, withSetNewPassword, withMFAResend,
  withForgotPasswordPage, withMFAVerification, withRequestPasswordReset,
  withMFAEmailRegistration,
} from 'containers/withQueries';
import withAuth from 'containers/withAuth';
import Layout from 'components/layout';

function ForgotPasswordPage(props) {
  const {
    location,
    continuePasswordReset,
    mfaResend,
    onTopBannerDismissClick,
    getOtpAddressValue,
    updateOtpStaticText,
    processing,
    otpResendProcessing,
    rejected,
    locked,
    topBannerText,
    validationError,
    emailValue,
    phoneNumber,
    mfaVerification,
    mfaEmailRegistration,
    staticText,
    locations,
    images,
    goTo,
    setNewPassword,
    forgotPasswordPageQuery,
    requestPasswordReset,
    track,
    hasStandaloneLoginPage,
  } = props;

  const {
    pageConfig: { header, footer }, customerProfile, refetch, componentConfig,
  } = forgotPasswordPageQuery;
  const homePage = header?.logo?.link?.href;

  const goToHomePage = () => {
    if (typeof window !== 'undefined') {
      window.location.href = homePage;
    }
  };

  const closeForgotPasswordPage = () => {
    goToHomePage();
  };

  useEffect(() => {
    continuePasswordReset();
  }, []);

  const onForgotPasswordSubmit = ({ email }) => {
    requestPasswordReset({ email });
  };

  const onResetLockedAccount = () => {
    goTo(locations.RESET_PASSWORD);
  };

  const onOTPVerificationSubmit = ({ otpCode }) => {
    mfaVerification({ otpCode });
  };

  const onOTPVerificationByEmailClick = (formData) => {
    mfaEmailRegistration({ email: formData?.email });
  };

  function onCreateNewPasswordPageSubmut({ password }) {
    setNewPassword({ password }, () => forgotPasswordLoginResetPasswordStep1Event(track));
  }

  let page;
  switch (location) {
    case locations.MFA_VERIFICATION:
      page = (
        <OTPPage
          overlay={false}
          heroImage={images?.LOGIN_PHONE_HERO?.image}
          heroImage2={images?.LOGIN_PHONE_HERO?.image}
          staticText={updateOtpStaticText(staticText, false)}
          handlers={{
            onResend: mfaResend,
            onSubmit: onOTPVerificationSubmit,
            onOTPVerificationByEmailClick,
            onTopBannerDismissClick,
            onReset: onResetLockedAccount,
          }}
          loading={processing}
          topBannerErrorButton={locked}
          topBannerNoLeftIcon
          otpResendLoading={otpResendProcessing}
          error={rejected}
          locked={locked}
          topBannerText={topBannerText}
          invalidOtpValue={validationError}
          otpReceiverId={getOtpAddressValue()}
          dataSelectorPrefix="forgot-password-otp"
        />
      );
      break;
    case locations.NEW_PASSWORD:
      page = (
        <CreateNewPasswordPage
          heroImage={images?.EMAIL_SIGNUP_HERO?.image}
          staticText={staticText}
          handlers={{
            onSubmit: onCreateNewPasswordPageSubmut,
          }}
          loading={processing}
        />
      );
      break;
    case locations.EMAIL_VERIFICATION:
      page = (
        <EmailVerificationPage
          isOpen
          hideOpenMailAppBtn
          heroImage={images?.LOGIN_EMAIL_HERO?.image}
          heroImage2={images?.LOGIN_EMAIL_HERO?.image}
          header={staticText.EMAIL_VERIFICATION_HEADER}
          descriptionSent={staticText.PASSWORD_RESET_EMAIL_DESCRIPTION}
          buttonText={staticText.OPEN_MAIL_APP_CTA}
          resendEmailLinkText={staticText.RESEND_EMAIL_TEXT_LINK}
          resendEmailText={staticText.EMAIL_RESEND_HEADER}
          resendEmailSent={staticText.RESEND_EMAIL_SENT}
          handlers={{
            onClose: closeForgotPasswordPage,
            onResendClick: () => onForgotPasswordSubmit({ email: emailValue }),
          }}
        />
      );
      break;
    case locations.RESET_PASSWORD:
      page = (
        <ForgotPassword
          isOpen
          heroImage={images?.EMAIL_SIGNUP_HERO?.image}
          staticText={staticText}
          handlers={{
            onSubmit: onForgotPasswordSubmit,
            onClose: closeForgotPasswordPage,
            onTopBannerDismissClick,
          }}
          email={emailValue}
          loading={processing}
          defaultPhoneValue={phoneNumber}
          topBannerText={topBannerText}
          hideRememberPasswordLink
          error={rejected}
        />
      );
      break;
    case locations.EXPIRED_LINK:
      page = (
        <ForgotExpiredLinkPage
          heroImage={images?.LOGIN_EMAIL_EXPIRED_HERO?.image}
          staticText={staticText}
          handlers={{
            onClick: goToHomePage,
          }}
        />
      );
      break;
    default:
      page = '';
      break;
  }

  useEffect(() => {
    loginLightboxLoadTrack(track, location);
  }, [location]);

  return (
    page ? (
      <Layout
        header={header}
        footer={footer}
        isFooterShown={false}
        variant="fullScreen"
        customerProfile={customerProfile}
        componentConfig={componentConfig}
        staticText={staticText}
        images={images}
        refetch={refetch}
        hasStandaloneLoginPage={hasStandaloneLoginPage}
      >
        {page}
      </Layout>
    ) : <LoadingPage />
  );
}

ForgotPasswordPage.defaultProps = {
  topBannerText: '',
  validationError: false,
  locked: false,
  emailValue: '',
  phoneNumber: '',
  track: null,
};

ForgotPasswordPage.propTypes = {
  track: func,
  location: string.isRequired,
  continuePasswordReset: func.isRequired,
  mfaResend: func.isRequired,
  onTopBannerDismissClick: func.isRequired,
  processing: bool.isRequired,
  otpResendProcessing: bool.isRequired,
  rejected: bool.isRequired,
  locked: bool,
  topBannerText: string,
  validationError: bool,
  emailValue: string,
  phoneNumber: string,
  goTo: func.isRequired,
  mfaVerification: func.isRequired,
  requestPasswordReset: func.isRequired,
  staticText: shape({}).isRequired,
  locations: shape({
    MFA_VERIFICATION: string,
    EXPIRED_LINK: string,
    NEW_PASSWORD: string,
  }).isRequired,
  images: shape({
    LOGIN_PHONE_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    LOGIN_EMAIL_EXPIRED_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    EMAIL_SIGNUP_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
  }).isRequired,
  setNewPassword: func.isRequired,
  forgotPasswordPageQuery: shape({
    pageConfig: shape({
      header: shape({
        logo: shape({
          link: shape({
            href: string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      footer: shape({
        logo: shape({
          image: shape({
            url: string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    customerProfile: shape({}).isRequired,
    refetch: func.isRequired,
  }).isRequired,
  hasStandaloneLoginPage: bool,
};

export default
withApplicationConfig(
  compose(
    withTracking,
    withContinuePasswordReset,
    withRequestPasswordReset,
    withMFAVerification,
    withMFAEmailRegistration,
    withMFAResend,
    withSetNewPassword,
    withForgotPasswordPage,
  )(
    withAuth(ForgotPasswordPage, 'FORGOT_PASSWORD')
  ), ['languages', 'contentstackKeys']
);
